import { observable } from "mobx";

import {
  apiGetPatientsPatientIdMeasurements,
  type MeasurementColumn,
  type MeasurementDay,
  type MeasurementSession,
  type MeasurementsResponse,
  type PPGSignalInfo,
  QuickLookTasks,
  SessionKind,
  type ValueCell,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { maybeReportError } from "../../../app/errors";
import { DataModel } from "../../../models/data-model";
import { PPGQuickLook } from "../../../models/quick-look-analyses";
import { browserTimezone, type Timezone } from "../../../utils/timezone";
import { todayInTimezone } from "../../daily-monitoring/daily-monitoring-logic";

/** A day row, or — under an expanded day — one of its sessions. */
export interface MeasurementRow {
  /** The local date for a day row; the session's index for a sub-row. */
  id: string;
  date: string;
  session: MeasurementSession;
  isSession: boolean;
  subRows?: MeasurementRow[];
}

export type SelectorName =
  | "hr_algorithm"
  | "hr_condition"
  | "spo2_condition"
  | "af_model";

export const SELECTOR_NAMES: SelectorName[] = [
  "hr_algorithm",
  "hr_condition",
  "spo2_condition",
  "af_model",
];

/** The page's view state that a link carries: the display selectors. */
export interface MeasurementsUrlState {
  choices: Partial<Record<SelectorName, string>>;
}

export const DAYS_PER_PAGE = 25;

/**
 * The endpoint caps a request at 366 days, and there is no date-range filter
 * yet (a follow-up), so the page covers the last year.
 */
const RANGE_DAYS = 365;

const SELECTORS_STORAGE_PREFIX = "measurements.selectors:";

/**
 * State of the Measurements page: the current page of day rows, the "full
 * measurements only" filter, and the display selectors (HR algorithm, PPG
 * condition, AF model). Selectors are a client-side choice over data the
 * payload already carries, so switching one re-renders without a request.
 */
export class Measurements {
  @observable accessor response: MeasurementsResponse | null = null;
  @observable accessor loading = false;
  /** Kept as the error, not its text, so the message follows the locale. */
  @observable accessor error: unknown = null;
  @observable accessor fullOnly = false;
  @observable accessor pagination: { pageIndex: number; pageSize: number };
  @observable private accessor choices: Partial<Record<SelectorName, string>>;
  /**
   * The selectors the URL speaks for: those a link arrived with, and those
   * changed on this page. A choice only remembered from storage still applies,
   * but arriving on the page doesn't write it into the URL.
   */
  @observable private accessor urlSelectors: ReadonlySet<SelectorName>;

  /** The rows DataTable renders: this page's days, sessions as sub-rows. */
  readonly table = new DataModel<MeasurementRow>([]);

  private readonly api: ApiConnector;
  private readonly patientId: number;
  private readonly timezone: Timezone;
  private readonly quickLooks = new Map<string, PPGQuickLook>();
  private requestId = 0;

  constructor(
    api: ApiConnector,
    patientId: number,
    initial: MeasurementsUrlState & { pageIndex: number },
  ) {
    this.api = api;
    this.patientId = patientId;
    this.timezone = browserTimezone();
    this.pagination = { pageIndex: initial.pageIndex, pageSize: DAYS_PER_PAGE };
    // A link's choices win for this view, but don't become the user's own:
    // only choosing in the toolbar writes to storage.
    this.choices = { ...readChoices(this.storageKey), ...initial.choices };
    this.urlSelectors = new Set(Object.keys(initial.choices) as SelectorName[]);
  }

  get columns(): MeasurementColumn[] {
    return this.response?.columns ?? [];
  }

  get totalDays(): number {
    return this.response?.pagination?.total_items ?? 0;
  }

  async load() {
    const requestId = ++this.requestId;
    this.loading = true;
    try {
      const { dateFrom, dateTo } = dateRange(this.timezone);
      const response = await apiGetPatientsPatientIdMeasurements({
        path: { patient_id: this.patientId },
        query: {
          timezone: this.timezone,
          date_from: dateFrom,
          date_to: dateTo,
          full_only: this.fullOnly,
          page: this.pagination.pageIndex + 1,
          page_size: this.pagination.pageSize,
        },
      });
      // A slower answer to a request the user has since moved past.
      if (requestId !== this.requestId) return;
      this.response = response.data;
      this.table.data = response.data.measurements.map(dayRow);
      this.error = null;
    } catch (error) {
      if (requestId !== this.requestId) return;
      maybeReportError(error);
      this.error = error;
    } finally {
      if (requestId === this.requestId) this.loading = false;
    }
  }

  /** Refetch after a write, past the client's response cache. */
  async refresh() {
    this.api.clearResponseCache();
    await this.load();
  }

  setPagination(pagination: { pageIndex: number; pageSize: number }) {
    if (
      this.response &&
      pagination.pageIndex === this.pagination.pageIndex &&
      pagination.pageSize === this.pagination.pageSize
    ) {
      return;
    }
    this.pagination = pagination;
    void this.load();
  }

  /** A server-side filter — it changes which days exist, so back to page 1. */
  setFullOnly(fullOnly: boolean) {
    this.fullOnly = fullOnly;
    this.pagination = { ...this.pagination, pageIndex: 0 };
    void this.load();
  }

  /**
   * What the URL should say: the choices it speaks for (see `urlSelectors`),
   * where they differ from the payload's default. Null until the first
   * response, since the defaults come from it — a link opened before then must
   * not be stripped.
   */
  get urlState(): MeasurementsUrlState | null {
    if (!this.response) return null;
    const choices: Partial<Record<SelectorName, string>> = {};
    for (const name of this.urlSelectors) {
      const value = this.selected(name);
      if (
        value !== undefined &&
        value !== this.response.selectors[name]?.default
      ) {
        choices[name] = value;
      }
    }
    return { choices };
  }

  /** The selector's options as the payload offers them for this range. */
  selectorOptions(name: SelectorName): string[] {
    return this.response?.selectors[name]?.options ?? [];
  }

  /**
   * The chosen value, falling back to the payload's default when the stored
   * choice isn't on offer for this patient and range.
   */
  selected(name: SelectorName): string | undefined {
    const selector = this.response?.selectors[name];
    const choice = this.choices[name];
    return choice !== undefined && selector?.options?.includes(choice)
      ? choice
      : selector?.default;
  }

  choose(name: SelectorName, value: string) {
    this.choices = { ...this.choices, [name]: value };
    this.urlSelectors = new Set([...this.urlSelectors, name]);
    writeChoices(this.storageKey, this.choices);
  }

  /**
   * The selectors that drive a column's values, as its metadata names them.
   * AF has no condition selector of its own and follows HR's, as in
   * `signalValue`.
   */
  columnSelectors(key: string): {
    variant?: SelectorName;
    condition?: SelectorName;
  } {
    const column = this.columns.find((item) => item.key === key);
    return {
      variant: column?.selector ? (column.selector as SelectorName) : undefined,
      condition: column?.condition_selector
        ? (column.condition_selector as SelectorName)
        : key === "af_probability"
          ? "hr_condition"
          : undefined,
    };
  }

  /**
   * A selector-driven cell (HR, SpO2, AF probability), which the API sends no
   * cell for: it is read from the session's signal in the selected condition,
   * under the selected variant. A standalone session has one signal outside
   * the protocol, so that signal is the answer whatever the condition. AF has
   * no condition selector of its own and follows HR's.
   */
  signalValue(
    session: MeasurementSession,
    columnKey: "hr" | "spo2" | "af_probability",
  ): ValueCell | undefined {
    const condition = this.selected(
      columnKey === "spo2" ? "spo2_condition" : "hr_condition",
    );
    const variant =
      columnKey === "hr"
        ? this.selected("hr_algorithm")
        : columnKey === "af_probability"
          ? this.selected("af_model")
          : "device";
    const signal = signalFor(session, condition);
    return variant === undefined
      ? undefined
      : signal?.derived?.[columnKey]?.[variant];
  }

  /**
   * One QuickLook model per session, kept across refetches so a submitted or
   * finished analysis doesn't reset when the page reloads its data.
   */
  quickLookFor(row: MeasurementRow): PPGQuickLook | null {
    const action = row.session.actions.quicklook_ppg;
    const uuids = action?.ppg_uuids ?? [];
    // Empty without PPG_RAW: the uuids are raw-signal identifiers.
    if (!action || uuids.length === 0) return null;
    const key = [...uuids].sort().join(",");
    // The API names the task (a single signal can't run the annotated-AUC
    // analysis); it is submitted as given, never chosen here.
    const task = action.task as QuickLookTasks;
    let quickLook = this.quickLooks.get(key);
    if (!quickLook) {
      quickLook = new PPGQuickLook(this.api, task, uuids);
      this.quickLooks.set(key, quickLook);
    }
    quickLook.syncSelection(task, uuids);
    return quickLook;
  }

  private get storageKey(): string {
    return SELECTORS_STORAGE_PREFIX + this.api.requireUserUUID();
  }
}

/**
 * Days are expandable when they hold more than one session. Every session,
 * the primary one included, gets a sub-row, so the expanded day lists the
 * whole day in order; the day row itself repeats the primary session.
 */
function dayRow(day: MeasurementDay): MeasurementRow {
  const primary = day.sessions[day.primary_session_index];
  return {
    id: day.date,
    date: day.date,
    session: primary,
    isSession: false,
    subRows:
      day.sessions.length > 1
        ? day.sessions.map((session, index) => ({
            id: String(index),
            date: day.date,
            session,
            isSession: true,
          }))
        : undefined,
  };
}

function signalFor(
  session: MeasurementSession,
  condition: string | undefined,
): PPGSignalInfo | undefined {
  const signals = session.ppg_signals ?? [];
  if (session.kind === SessionKind.standalone) return signals[0];
  // Signals come oldest first; the newest in the condition wins, as the API
  // picks cells.
  return signals
    .filter((signal) => signal.measurement_condition === condition)
    .pop();
}

function dateRange(timezone: string): { dateFrom: string; dateTo: string } {
  const dateTo = todayInTimezone(timezone);
  const from = new Date(`${dateTo}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - (RANGE_DAYS - 1));
  return { dateFrom: from.toISOString().slice(0, 10), dateTo };
}

function readChoices(key: string): Partial<Record<SelectorName, string>> {
  try {
    const raw = localStorage.getItem(key);
    return raw
      ? (JSON.parse(raw) as Partial<Record<SelectorName, string>>)
      : {};
  } catch {
    return {};
  }
}

function writeChoices(
  key: string,
  choices: Partial<Record<SelectorName, string>>,
) {
  try {
    localStorage.setItem(key, JSON.stringify(choices));
  } catch (error) {
    console.warn("Cannot persist measurement selectors", error);
  }
}
